/** Krátký režim na konec promptu — Qwen jinak utopí Live v dlouhém system textu. */

/**
 * Co zrovna platí, podle stavu spodní lišty:
 *  - `auto`        = A zapnuté, práce i karty běží samy (default)
 *  - `auto-commit` = A + G, ke změnám patří i git checkpoint
 *  - `manual`      = A vypnuté (M) — plán/patch/commit karty čekají na klik
 *  - `consult`     = zámek (pojistka) — agent jen konzultuje, nic nespouští
 */
export type CodableStickyMode = 'auto' | 'auto-commit' | 'manual' | 'consult';

/**
 * `slug` = projekt, jehož dveřmi se zrovna mluví.
 *
 * Řádek musí zůstat až na konci promptu. Dřív se hranice psala jen do hlavy
 * system preamble a malý model (gemma-4-26b-a4b, 2026-09-10) ji ztratil: v
 * Codable chatu se choval, že má dosah kamkoliv na platformě. Filipův správný
 * model: „míra ovládání ACM je daná tím, přes jaký endpoint jsi připojený… a
 * ACL, který tam nafasuješ" — tentýž agent platformu zná a povídá o ní, jen
 * volá to, na co má nárok.
 *
 * Proto hranice říká obojí v jedné větě: vědět a vyprávět smí, zasahovat jen
 * tady. Samotné varování nestačí — to vede malý model buď k mlčení, nebo ke
 * zkouštění štěstí.
 */
export function codableWorkModeSticky(
  mode: CodableStickyMode,
  slug?: string,
): string {
  const sandbox = slug
    ? `HRANICE: O platformě víš a povídej si o ní — jen tyhle dveře jsou projekt \`${slug}\`. ` +
      `Operace mimo něj nedělej ani netvař, že jsou udělané; mají jiné dveře (běžný ACM chat), ` +
      `kde sedí agent s jinými nároky.`
    : 'HRANICE: O platformě víš a povídej si o ní — jen tyhle dveře jsou sandbox Codable, operace mimo něj patří do běžného ACM chatu.';
  const lines =
    mode === 'auto-commit'
      ? [
        'REŽIM TÉTO ZPRÁVY: AUTO + GIT CHECKPOINT.',
        'Úprava = nativní codable_workspace_replace / codable_workspace_write. To je DEV workspace + náhled, ne git.',
        'GitLab repo se změní AŽ po úspěšném codable_commit. Write ≠ commit. Koukání do git logu po write je k ničemu.',
        '„Upravil jsem“ bez tool resultu ok=true z workspace_replace/write je nepravda.',
      ]
      : mode === 'manual'
      ? [
        'REŽIM TÉTO ZPRÁVY: M — TAHY SE SCHVALUJÍ.',
        'Do DEV workspace psát můžeš (codable_workspace_replace / codable_workspace_write), to jde hned.',
        'Ale na konci tahu shrň, co jsi změnil, a nech to posoudit: plán (```plan) ani commit (```commit) neposílej jako hotovou věc a sám do gitu nekomituj.',
        'Nesepisuj práci jako hotovou před tool resultem ok=true.',
      ]
      : mode === 'consult'
      ? [
        'REŽIM TÉTO ZPRÁVY: KONZULTACE (zamčeno).',
        'Uživatel chtěl promluvit, ne měnit nic. Odpověz, vysvětli, kód napiš do zprávy, když pomůže.',
        'Nespouštěj žádné operace. Chce-li měnit, ať klikne na odomknutí.',
      ]
      : [
        'REŽIM TÉTO ZPRÁVY: AUTO (jen DEV workspace).',
        'Úprava = nativní codable_workspace_replace / codable_workspace_write. Hned v náhledu.',
        'GitLab repo se NEMĚNÍ. To není chyba a není to překvapení. Commit až když uživatel chce checkpoint.',
        'Když uživatel řekne „není to v repu“: správně — AUTO není git. Ověř workspace_status / preview, ne historii commitů.',
        '„Upravil jsem“ bez tool resultu ok=true z workspace_replace/write je nepravda.',
      ];
  return [...lines, sandbox].join('\n');
}
